import { useCallback, useState } from 'react'

// --- Difference types enum ---
export const DIFF_TYPES = {
  TEXT_CHANGE: 'text_change',
  VISUAL_CHANGE: 'visual_change',
  CHECKBOX_CHANGE: 'checkbox_change',
  POSSIBLE_CHECKBOX_CHANGE: 'possible_checkbox_change',
  FORMATTING_CHANGE: 'formatting_change',
}

export const visibleContentDifferences = [
  DIFF_TYPES.VISUAL_CHANGE,
  DIFF_TYPES.CHECKBOX_CHANGE,
  DIFF_TYPES.POSSIBLE_CHECKBOX_CHANGE,
  DIFF_TYPES.FORMATTING_CHANGE,
]

// --- Deterministic-ish constants ---
const DETERMINISTIC_CONFIG = {
  FIXED_SCALE: 3.0, // Higher scale = less antialiasing artifacts (3.0 optimal for forms)
  TOLERANCE: 160, // Increased tolerance to ignore minor antialiasing differences (was 60)
  MIN_GAP: 10,
  MAX_DIFFERENCES: 1000,
  MAX_RECTANGLES: 500,
  MAX_ITERATIONS: 50,
  MIN_REGION_SIZE: 25, // Ignore very small regions (likely antialiasing noise)
  MAX_REGION_SIZE: 10000,
  MARGIN_BETWEEN_PAGES: 5,
  TEXT_ROTATION_THRESHOLD: 0.1,
  POSITION_TOLERANCE: 2,
  SUPER_SAMPLE_FACTOR: 1, // No supersampling - direct 1:1 rendering for exact pixel match
  SNAP_PRECISION: 10000, // Increased precision for more exact scale snapping
  TEXT_INTERSECTION_THRESHOLD: 0.5, // 50% - Text must overlap region by at least this much (optimal for checkboxes)
}

// Export the detection scale for coordinate conversion
export const DETECTION_SCALE = DETERMINISTIC_CONFIG.FIXED_SCALE

// --- Utilities ---

// Wait for web fonts if any; reduces races between font loading and rendering.
const waitForFontsIfAny = async () => {
  try {
    if ('fonts' in document && (document).fonts.ready) {
      await (document).fonts.ready
    }
  } catch {
    // No-op if not supported
  }
}

// Build a stable viewport whose resulting pixel dimensions are integers.
const getStableViewport = (page, baseScale = DETERMINISTIC_CONFIG.FIXED_SCALE) => {
  const baseVp = page.getViewport({ scale: 1, rotation: 0 })
  const targetWidthPx = Math.round(baseVp.width * baseScale)
  const snappedScale =
    Math.round((targetWidthPx / baseVp.width) * DETERMINISTIC_CONFIG.SNAP_PRECISION) /
    DETERMINISTIC_CONFIG.SNAP_PRECISION
  return page.getViewport({ scale: snappedScale, rotation: 0 })
}

// Deterministic page rendering with supersampling + nearest-neighbor downscale.
const renderPageToCanvas = async (page, scale = DETERMINISTIC_CONFIG.FIXED_SCALE) => {
  await waitForFontsIfAny()
  try {
    // Warm-up parsing to reduce timing variability
    await page.getOperatorList()
  } catch {}

  const viewport = getStableViewport(page, scale)

  const SS = Math.max(1, DETERMINISTIC_CONFIG.SUPER_SAMPLE_FACTOR | 0)
  const renderW = Math.round(viewport.width) * SS
  const renderH = Math.round(viewport.height) * SS

  const renderCanvas = document.createElement('canvas')
  renderCanvas.width = renderW
  renderCanvas.height = renderH
  renderCanvas.style.width = `${renderW}px`
  renderCanvas.style.height = `${renderH}px`

  const renderCtx = renderCanvas.getContext('2d', {
    alpha: false,
    desynchronized: false,
    colorSpace: 'srgb',
    willReadFrequently: true,
  })

  // Disable ALL anti-aliasing and smoothing for exact pixel rendering
  renderCtx.imageSmoothingEnabled = false
  renderCtx.imageSmoothingQuality = 'low'
  // Cross-browser support for disabling image smoothing
  if ('mozImageSmoothingEnabled' in renderCtx) {
    renderCtx.mozImageSmoothingEnabled = false
  }
  if ('webkitImageSmoothingEnabled' in renderCtx) {
    renderCtx.webkitImageSmoothingEnabled = false
  }
  if ('msImageSmoothingEnabled' in renderCtx) {
    renderCtx.msImageSmoothingEnabled = false
  }

  // White background for consistency
  renderCtx.save()
  renderCtx.fillStyle = '#FFFFFF'
  renderCtx.fillRect(0, 0, renderW, renderH)

  // Note: rebuild viewport with SS applied
  const ssViewport = new viewport.constructor({
    viewBox: viewport.viewBox,
    scale: viewport.scale * SS,
    rotation: viewport.rotation,
    dontFlip: viewport.dontFlip ?? false,
    offsetX: 0,
    offsetY: 0,
  })

  const renderContext = {
    canvasContext: renderCtx,
    viewport: ssViewport,
    intent: 'display',
    enableXfa: false,
    annotationMode: 0, // no annotation layer
    renderInteractiveForms: false,
    enableWebGL: false,
    // Close non-deterministic code paths for deterministic rendering
    disableAutoFetch: true,
    disableFontFace: false, // Keep embedded fonts for consistent rendering
    disableRange: true,
    disableStream: true,
    disableCreateObjectURL: true,
    textContentStreamFactory: null,
    imageLayer: null,
    useSystemFonts: false, // Force embedded fonts only
  }

  await page.render(renderContext).promise
  renderCtx.restore()

  // Downscale using nearest-neighbor if supersampling is enabled (currently disabled)
  if (SS > 1) {
    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = Math.round(viewport.width)
    finalCanvas.height = Math.round(viewport.height)
    finalCanvas.style.width = `${finalCanvas.width}px`
    finalCanvas.style.height = `${finalCanvas.height}px`

    const downCtx = finalCanvas.getContext('2d', {
      alpha: false,
      desynchronized: false,
      colorSpace: 'srgb',
      willReadFrequently: true,
    })

    // Disable smoothing for nearest-neighbor downscaling
    downCtx.imageSmoothingEnabled = false
    downCtx.imageSmoothingQuality = 'low'
    if ('mozImageSmoothingEnabled' in downCtx) {
      downCtx.mozImageSmoothingEnabled = false
    }
    if ('webkitImageSmoothingEnabled' in downCtx) {
      downCtx.webkitImageSmoothingEnabled = false
    }
    if ('msImageSmoothingEnabled' in downCtx) {
      downCtx.msImageSmoothingEnabled = false
    }

    downCtx.drawImage(renderCanvas, 0, 0, renderW, renderH, 0, 0, finalCanvas.width, finalCanvas.height)
    return finalCanvas
  }

  return renderCanvas
}

const getImageData = (canvas) => {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

const buildDiffId = (pageIndex, idx) => `pixel-diff-${pageIndex}-${idx}`

// Heuristic: small, almost square shapes likely are checkboxes.
// Sizes are scaled based on FIXED_SCALE for accurate detection
const isCheckboxSize = (width, height) => {
  const aspectRatio = Math.max(width, height) / Math.min(width, height)
  const isSquareish = aspectRatio <= 1.3 // Slightly more lenient

  // Base sizes for scale 1.0, then multiply by current scale
  const scale = DETERMINISTIC_CONFIG.FIXED_SCALE
  const minSize = Math.round(6 * scale) // 18 at scale 3.0
  const maxSize = Math.round(14 * scale) // 42 at scale 3.0 (increased for larger forms)

  const isInSizeRange = width >= minSize && width <= maxSize && height >= minSize && height <= maxSize
  return isSquareish && isInSizeRange
}

// Classify a difference using text signals and geometry.
const classifyDifference = (newText, oldText, width, height) => {
  const normalizedNewText = newText || ''
  const normalizedOldText = oldText || ''

  const cleanNewText = normalizedNewText === 'No text found' ? '' : normalizedNewText
  const cleanOldText = normalizedOldText === 'No text found' ? '' : normalizedOldText

  // Explicit checkbox toggles ("X" appearing/disappearing)
  const isNewCheckbox = cleanNewText === 'X' && cleanOldText === ''
  const isOldCheckbox = cleanOldText === 'X' && cleanNewText === ''
  if (isNewCheckbox || isOldCheckbox) {
    return { type: DIFF_TYPES.CHECKBOX_CHANGE, contentValue: 'Visual changes detected (checkbox)' }
  }

  // Text changed
  if ((cleanNewText !== '' || cleanOldText !== '') && cleanNewText !== cleanOldText) {
    return { type: DIFF_TYPES.TEXT_CHANGE, contentValue: 'Text content changed' }
  }

  // Same text but looks visually different (formatting)
  if (cleanNewText !== '' && cleanOldText !== '' && cleanNewText === cleanOldText) {
    return { type: DIFF_TYPES.FORMATTING_CHANGE, contentValue: 'Visual formatting changes detected' }
  }

  // No text: if geometry looks like a checkbox, mark possible checkbox change
  if (
    (cleanNewText === '' || cleanNewText === 'No text found') &&
    (cleanOldText === '' || cleanOldText === 'No text found') &&
    width &&
    height &&
    isCheckboxSize(width, height)
  ) {
    return { type: DIFF_TYPES.POSSIBLE_CHECKBOX_CHANGE, contentValue: 'Visual changes detected (checkbox)' }
  }

  // Generic visual change without text
  return { type: DIFF_TYPES.VISUAL_CHANGE, contentValue: 'Visual changes detected (no text)' }
}

// Extracts text inside a given rectangle on a PDF page (viewport-space coords)
const extractTextFromRegion = async (
  side,
  pageIndex,
  page,
  region,
  scale = 1.0
) => {
  try {
    if (!page || !region) { return '' }

    const viewport = page.getViewport({ scale })
    const textContent = await page.getTextContent()
    if (!textContent?.items?.length) { return '' }

    const { x, y, width, height } = region
    const extractedText = []

    textContent.items.forEach((item) => {
      if (!item.str || !item.transform || item.str.trim() === '') { return }

      // Transform is [scaleX, skewY, skewX, scaleY, translateX, translateY]
      const [scaleX, , , scaleY, translateX, translateY] = item.transform

      // Skip rotated/near-degenerate glyphs
      if (
        Math.abs(scaleX) < DETERMINISTIC_CONFIG.TEXT_ROTATION_THRESHOLD ||
        Math.abs(scaleY) < DETERMINISTIC_CONFIG.TEXT_ROTATION_THRESHOLD
      ) {
        return
      }

      // Compute item box in viewport coords
      // Scale text coordinates to match the viewport scale used for rendering
      const marginBetweenPages = DETERMINISTIC_CONFIG.MARGIN_BETWEEN_PAGES
      const itemX = translateX * scale
      const itemY = viewport.height - (translateY * scale) - marginBetweenPages
      const itemWidth = item.width * scale
      const itemHeight = item.height * scale

      const textLeft = itemX
      const textRight = itemX + itemWidth
      const textTop = itemY
      const textBottom = itemY + itemHeight

      const regionLeft = x
      const regionRight = x + width
      const regionTop = y
      const regionBottom = y + height

      // Calculate overlap area
      const overlapLeft = Math.max(textLeft, regionLeft)
      const overlapRight = Math.min(textRight, regionRight)
      const overlapTop = Math.max(textTop, regionTop)
      const overlapBottom = Math.min(textBottom, regionBottom)

      // Check if there's basic intersection first
      const hasIntersection = overlapLeft < overlapRight && overlapTop < overlapBottom

      if (hasIntersection) {
        // Calculate areas
        const overlapWidth = overlapRight - overlapLeft
        const overlapHeight = overlapBottom - overlapTop
        const overlapArea = overlapWidth * overlapHeight
        const textArea = itemWidth * itemHeight

        // Calculate intersection percentage of the text
        const intersectionPercentage = textArea > 0 ? (overlapArea / textArea) : 0

        // Only include if at least the threshold percentage of text is within the region
        if (intersectionPercentage >= DETERMINISTIC_CONFIG.TEXT_INTERSECTION_THRESHOLD) {
          extractedText.push({
            text: item.str.trim(),
            x: itemX,
            y: itemY,
            width: itemWidth,
            height: itemHeight,
            associatedRegion: region,
          })
        }
      }
    })

    if (!extractedText.length) { return '' }

    // Sort by reading order: top-to-bottom, then left-to-right (with a small y tolerance)
    extractedText.sort((a, b) => {
      const yDiff = a.y - b.y
      if (Math.abs(yDiff) > DETERMINISTIC_CONFIG.POSITION_TOLERANCE) { return yDiff }
      return a.x - b.x
    })

    // Join conservatively to avoid spacing-based nondeterminism
    return extractedText
      .map((it) => it.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
  } catch (err) {
    console.error('Error extracting text from region:', err)
    return ''
  }
}

// --- Diffing ---

// Per-pixel difference metric (sum of channel deltas by default)
const perPixelDiff = (
  r1,
  g1,
  b1,
  r2,
  g2,
  b2,
  metric = 'sum'
) => {
  const dr = Math.abs(r1 - r2)
  const dg = Math.abs(g1 - g2)
  const db = Math.abs(b1 - b2)
  return metric === 'max' ? Math.max(dr, dg, db) : dr + dg + db
}

// Simple AA-speckle filter: if almost all neighbors are similar, treat the current pixel as anti-aliased noise.
const looksLikeAA = (
  x,
  y,
  width,
  height,
  data1,
  data2,
  tol
) => {
  let neighbors = 0
  let diffs = 0
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) { continue }
      const nx = x + dx
      const ny = y + dy
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) { continue }
      neighbors++
      const i = (ny * width + nx) * 4
      const d = perPixelDiff(
        data1[i],
        data1[i + 1],
        data1[i + 2],
        data2[i],
        data2[i + 1],
        data2[i + 2],
        'sum'
      )
      if (d > tol) { diffs++ }
    }
  }
  // If fewer than 20% neighbors exceed tolerance, likely AA speckle
  return neighbors > 0 && diffs / neighbors < 0.2
}

const compareImageData = (imageData1, imageData2, tolerance = DETERMINISTIC_CONFIG.TOLERANCE) => {
  const { width, height, data: data1 } = imageData1
  const { data: data2 } = imageData2

  const differences = []
  const visited = new Set()

  let shouldBreak = false
  for (let y = 0; y < height && !shouldBreak; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4

      const d = perPixelDiff(
        data1[index],
        data1[index + 1],
        data1[index + 2],
        data2[index],
        data2[index + 1],
        data2[index + 2],
        'sum'
      )

      if (d > tolerance) {
        // Ignore isolated AA speckles
        if (looksLikeAA(x, y, width, height, data1, data2, tolerance)) { continue }

        const region = findConnectedRegion(x, y, width, height, data1, data2, tolerance, visited)
        if (region.length > DETERMINISTIC_CONFIG.MIN_REGION_SIZE) {
          differences.push(region)
        }

        if (differences.length > DETERMINISTIC_CONFIG.MAX_DIFFERENCES) {
          console.warn('Too many differences detected, truncating for performance')
          shouldBreak = true
          break
        }
      }
    }
  }

  return differences
}

const findConnectedRegion = (
  startX,
  startY,
  width,
  height,
  data1,
  data2,
  tolerance,
  visited
) => {
  const stack = [[startX, startY]]
  const region = []
  const maxRegionSize = DETERMINISTIC_CONFIG.MAX_REGION_SIZE

  while (stack.length > 0 && region.length < maxRegionSize) {
    const [x, y] = stack.pop()
    const key = `${x},${y}`
    if (visited.has(key) || x < 0 || x >= width || y < 0 || y >= height) { continue }

    visited.add(key)

    const index = (y * width + x) * 4
    const d = perPixelDiff(
      data1[index],
      data1[index + 1],
      data1[index + 2],
      data2[index],
      data2[index + 1],
      data2[index + 2],
      'sum'
    )

    if (d > tolerance) {
      region.push([x, y])
      // Deterministic neighbor order
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1])
    }
  }

  // Sort region points deterministically (top-to-bottom, left-to-right)
  return region.sort((a, b) => {
    const yDiff = a[1] - b[1]
    if (Math.abs(yDiff) > DETERMINISTIC_CONFIG.POSITION_TOLERANCE) { return yDiff }
    return a[0] - b[0]
  })
}

// Group pixel regions into rectangles, then unify overlapping/nearby rectangles
const groupDifferencesIntoRectangles = (differences, minGap = DETERMINISTIC_CONFIG.MIN_GAP) => {
  if (!differences.length) { return [] }

  const sortedDifferences = [...differences].sort((a, b) => {
    if (!a.length || !b.length) { return 0 }
    const aFirst = a[0]
    const bFirst = b[0]
    const yDiff = aFirst[1] - bFirst[1]
    if (Math.abs(yDiff) > DETERMINISTIC_CONFIG.POSITION_TOLERANCE) { return yDiff }
    return aFirst[0] - bFirst[0]
  })

  const limitedDifferences =
    sortedDifferences.length > DETERMINISTIC_CONFIG.MAX_DIFFERENCES
      ? sortedDifferences.slice(0, DETERMINISTIC_CONFIG.MAX_DIFFERENCES)
      : sortedDifferences

  const initialRectangles = []

  limitedDifferences.forEach((region) => {
    if (!region.length) { return }

    let minX = region[0][0]
    let maxX = region[0][0]
    let minY = region[0][1]
    let maxY = region[0][1]

    for (let i = 1; i < region.length; i++) {
      const x = region[i][0]
      const y = region[i][1]
      if (x < minX) { minX = x }
      if (x > maxX) { maxX = x }
      if (y < minY) { minY = y }
      if (y > maxY) { maxY = y }
    }

    initialRectangles.push({
      x: minX,
      y: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
    })
  })

  return unifyRectangles(initialRectangles, minGap)
}

const unifyRectangles = (
  rectangles,
  minGap = DETERMINISTIC_CONFIG.MIN_GAP
) => {
  if (!rectangles.length) { return [] }

  const sortedRectangles = [...rectangles].sort((a, b) => {
    const yDiff = a.y - b.y
    if (Math.abs(yDiff) > DETERMINISTIC_CONFIG.POSITION_TOLERANCE) { return yDiff }
    return a.x - b.x
  })

  const maxRectangles = DETERMINISTIC_CONFIG.MAX_RECTANGLES
  let unified =
    sortedRectangles.length > maxRectangles ? sortedRectangles.slice(0, maxRectangles) : [...sortedRectangles]

  let hasChanges = true
  let iterations = 0
  const maxIterations = Math.min(DETERMINISTIC_CONFIG.MAX_ITERATIONS, unified.length)

  while (hasChanges && iterations < maxIterations) {
    hasChanges = false
    iterations++

    const newUnified = []
    const processed = new Set()

    for (let i = 0; i < unified.length; i++) {
      if (processed.has(i)) { continue }
      let currentRect = unified[i]

      for (let j = i + 1; j < unified.length; j++) {
        if (processed.has(j)) { continue }
        if (rectanglesShouldMerge(currentRect, unified[j], minGap)) {
          currentRect = mergeRectangles(currentRect, unified[j])
          processed.add(j)
          hasChanges = true
        }
      }

      newUnified.push(currentRect)
      processed.add(i)
    }

    unified = newUnified
  }

  return unified
}

const rectanglesShouldMerge = (
  rect1,
  rect2,
  minGap
) => {
  return !(
    rect1.x + rect1.width + minGap < rect2.x ||
    rect2.x + rect2.width + minGap < rect1.x ||
    rect1.y + rect1.height + minGap < rect2.y ||
    rect2.y + rect2.height + minGap < rect1.y
  )
}

const mergeRectangles = (
  rect1,
  rect2
) => {
  const minX = Math.min(rect1.x, rect2.x)
  const maxX = Math.max(rect1.x + rect1.width - 1, rect2.x + rect2.width - 1)
  const minY = Math.min(rect1.y, rect2.y)
  const maxY = Math.max(rect1.y + rect1.height - 1, rect2.y + rect2.height - 1)

  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  }
}

// Remove duplicate formatting changes with identical text (keep only first occurrence)
const removeDuplicatesDifferences = (
  allDiffs,
  pageIndex,
  pageNumber,
  differences,
) => {
  const uniqueDiffs = []

  const sorted = [...allDiffs].sort((a, b) => a.pageIndex - b.pageIndex)

  for (let i = 0; i < sorted.length; i++) {
    const diffsByPage = sorted[i].differences

    const sortedDiffsByPage = [...diffsByPage].sort((a, b) => {
      const yDiff = a.y - b.y
      if (Math.abs(yDiff) > DETERMINISTIC_CONFIG.POSITION_TOLERANCE) { return yDiff }
      return a.x - b.x
    })

    const filtered = sortedDiffsByPage.filter((diff, index, self) => {
      if (
        diff.type === DIFF_TYPES.FORMATTING_CHANGE &&
        diff.newText &&
        diff.oldText &&
        diff.newText !== 'No text found' &&
        diff.oldText !== 'No text found' &&
        diff.newText === diff.oldText
      ) {
        return (
          index ===
          self.findIndex(
            (t) =>
              t.type === DIFF_TYPES.FORMATTING_CHANGE &&
              t.newText === diff.newText &&
              t.oldText === diff.oldText &&
              t.newText === t.oldText
          )
        )
      }
      return true
    })

    uniqueDiffs.push({
      pageIndex: sorted[i].pageIndex,
      pageNumber: sorted[i].pageNumber,
      differences: filtered,
    })
  }
  return uniqueDiffs
}

// Add rules to exclude differences (filter out antialiasing artifacts)
const shouldExclude = (rect, newText, oldText) => {
  // Exclude very thin horizontal lines (likely antialiasing on borders)
  const scale = DETERMINISTIC_CONFIG.FIXED_SCALE
  if (rect.height <= Math.round(3 * scale)) {
    return true
  }

  // Exclude very thin vertical lines (likely antialiasing on borders)
  if (rect.width <= Math.round(3 * scale)) {
    return true
  }

  // Exclude very small rectangles without text (likely antialiasing noise)
  // But don't exclude potential checkboxes
  const area = rect.width * rect.height
  const hasNoText = (!newText || newText === 'No text found') &&
                    (!oldText || oldText === 'No text found')
  const minCheckboxSize = Math.round(6 * scale)
  const isNotCheckboxSized = rect.width < minCheckboxSize || rect.height < minCheckboxSize

  // Minimum area threshold scaled with FIXED_SCALE
  const minAreaThreshold = Math.round(100 * scale * scale) // ~900 at scale 3.0
  if (area < minAreaThreshold && hasNoText && isNotCheckboxSized) {
    return true
  }

  return false
}

// --- React hook: usePixelDiff ---

export const usePixelDiff = () => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [pixelDiffs, setPixelDiffs] = useState([])

  const comparePages = useCallback(
    async (pdf1, pdf2, alignedPages) => {
      if (!pdf1 || !pdf2) { return [] }

      setIsProcessing(true)
      setProgress(0)

      try {
        const allDiffs = []
        const totalPages = alignedPages.length

        const sortedAlignedPages = [...alignedPages].sort((a, b) => a.pageIndex - b.pageIndex)

        // Sequential processing for stability
        for (let i = 0; i < totalPages; i++) {
          const aligned = sortedAlignedPages[i]

          if (aligned.type === 'matched') {
            try {
              const page1 = await pdf1.getPage(aligned.pageNum1)
              const page2 = await pdf2.getPage(aligned.pageNum2)

              const canvas1 = await renderPageToCanvas(page1, DETERMINISTIC_CONFIG.FIXED_SCALE)
              const canvas2 = await renderPageToCanvas(page2, DETERMINISTIC_CONFIG.FIXED_SCALE)

              const imageData1 = getImageData(canvas1)
              const imageData2 = getImageData(canvas2)

              const differences = compareImageData(imageData1, imageData2, DETERMINISTIC_CONFIG.TOLERANCE)
              const rectangles = groupDifferencesIntoRectangles(differences, DETERMINISTIC_CONFIG.MIN_GAP)

              if (rectangles.length > 0) {
                const differencesWithText = []

                for (let idx = 0; idx < rectangles.length; idx++) {
                  const rect = rectangles[idx]
                  const newText = (await extractTextFromRegion('left', i, page1, rect, DETERMINISTIC_CONFIG.FIXED_SCALE)) || 'No text found'
                  const oldText = (await extractTextFromRegion('right', i, page2, rect, DETERMINISTIC_CONFIG.FIXED_SCALE)) || 'No text found'

                  const classification = classifyDifference(newText, oldText, rect.width, rect.height)

                  // Filter out antialiasing artifacts and noise
                  if (shouldExclude(rect, newText, oldText)) {
                    continue
                  }

                  differencesWithText.push({
                    id: buildDiffId(i, idx),
                    type: classification.type,
                    contentValue: classification.contentValue,
                    newText,
                    oldText,
                    ...rect,
                  })
                }

                if (differencesWithText.length > 0) {
                  allDiffs.push({
                    pageIndex: i,
                    pageNumber: aligned.pageNum1,
                    differences: differencesWithText,
                  })
                }
              }

              setProgress(((i + 1) / totalPages) * 100)
            } catch (error) {
              console.error(`Error comparing page ${i}:`, error)
            }
          }
        }

        const uniqueDiffs = removeDuplicatesDifferences(allDiffs)
        setProgress(100)
        setPixelDiffs(uniqueDiffs)
        return uniqueDiffs
      } catch (error) {
        console.error('Error in pixel comparison:', error)
        return []
      } finally {
        setIsProcessing(false)
      }
    },
    []
  )

  return {
    comparePages,
    pixelDiffs,
    isProcessing,
    progress,
  }
}
